import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  EmbeddedViewRef,
  Injectable,
  Injector,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { TextService } from '../core/service/text.service';
import { IGeneralText } from 'src/app/core/interfaces/generalText.interface';

export type MessageServiceAction =
  | 'add'
  | 'update'
  | 'delete'
  | 'download'
  | 'get';
type Position =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left'
  | 'top-center'
  | 'bottom-center'
  | 'center';
type Severity = 'success' | 'info' | 'warn' | 'error';
type ActionLabels = Record<MessageServiceAction, { label: string }>;

@Injectable({
  providedIn: 'root',
})
export class CommonMessageService {
  private key = 'common-message';
  private toastRef: ComponentRef<Toast>;
  private isToastInitialized = false;
  generalTexts: IGeneralText = {
    success: { labelId: 'L_SUCCESSFUL' },
    error: { labelId: 'L_ERROR_TITLE' },
    warn: { labelId: 'L_WARNING' },
    info: { labelId: 'L_INFORMATION' },
    RECORD_UPDATED: { labelId: 'L_RECORD_UPDATED' },
    RECORD_UPDATE_ERROR: { labelId: 'L_RECORD_UPDATE_ERROR' },
    RECORD_CREATE_ERROR: { labelId: 'L_RECORD_CREATE_ERROR' },
    RECORD_GET_ERROR: { labelId: 'L_RECORD_GET_ERROR' },
    L_OPERATION_FAILED: { labelId: 'L_OPERATION_FAILED' },
    RECORD_DELETE_ERROR: { labelId: 'L_RECORD_DELETE_ERROR' },
    CREATED: { labelId: 'L_CREATED' },
    UPDATED: { labelId: 'L_RECORD_UPDATED' },
    SAVED: { labelId: 'L_SAVED' },
    DELETED: { labelId: 'L_DELETED' },
    FILE_RECORD_CREATED: { labelId: 'L_FILE_RECORD_CREATED' },
    FILE_RECORD_UPDATED: { labelId: 'L_FILE_RECORD_UPDATED' },
    FILE_RECORD_DELETED: { labelId: 'L_FILE_RECORD_DELETED' },
    FILE_RECORD_CREATE_ERROR: { labelId: 'L_FILE_RECORD_CREATE_ERROR' },
    FILE_RECORD_UPDATE_ERROR: { labelId: 'L_FILE_RECORD_UPDATE_ERROR' },
    FILE_RECORD_DELETE_ERROR: { labelId: 'L_FILE_RECORD_DELETE_ERROR' },
    FILL_ALL_REQUIRED_FIELDS: { labelId: 'L_FILL_ALL_REQUIRED_FIELDS' },
  };

  constructor(
    private messageService: MessageService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
    private textService: TextService
  ) {
    this.initializeToastComponent();
  }

  private initializeToastComponent(position: Position = 'top-right') {
    if (!this.isToastInitialized) {
      const componentFactory =
        this.componentFactoryResolver.resolveComponentFactory(Toast);
      this.toastRef = componentFactory.create(this.injector);
      this.appRef.attachView(this.toastRef.hostView);
      const domElem = (this.toastRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;
      document.body.appendChild(domElem);
      this.toastRef.instance.key = this.key;
      this.toastRef.instance.position = position;
      this.isToastInitialized = true;
    }
  }

  private prepareToast(position: Position = 'top-right') {
    this.initializeToastComponent(position);
    this.textService.convert(this.generalTexts).subscribe((res) => {
      this.generalTexts = res;
    });
  }

  private addMessage(severity: Severity, detail: string, sticky: boolean) {
    this.messageService.clear(this.key);
    this.messageService.add({
      detail,
      severity,
      sticky,
      key: this.key,
      summary: this.generalTexts[severity].label,
      life: 3000,
    });
  }

  warn(
    detail = this.generalTexts.FILL_ALL_REQUIRED_FIELDS.label,
    sticky = false,
    position?: Position
  ): void {
    this.prepareToast(position);
    this.addMessage('warn', detail, sticky);
  }

  info(detail: string, sticky = false, position?: Position): void {
    this.prepareToast(position);
    this.addMessage('info', detail, sticky);
  }

  success(
    action: MessageServiceAction = 'add',
    detail?: string,
    isFile = false,
    sticky = false,
    position?: Position
  ): void {
    this.prepareToast(position);
    const actionLabels = {
      add: 'CREATED',
      update: 'UPDATED',
      delete: 'DELETED',
    };
    const actionLabel = actionLabels[action];
    const detailLabel = !isFile
      ? this.generalTexts[`${actionLabel}`].label
      : this.generalTexts[`FILE_RECORD_${actionLabel}`].label;
    this.addMessage('success', detail || detailLabel, sticky);
  }

  error(
    action: MessageServiceAction = 'add',
    detail?: string,
    isFile = false,
    sticky = false,
    position?: Position
  ): void {
    this.prepareToast(position);
    const actionLabels: ActionLabels = {
      add: {
        label: !isFile
          ? this.generalTexts.RECORD_CREATE_ERROR.label
          : this.generalTexts.FILE_RECORD_CREATE_ERROR.label,
      },
      update: {
        label: !isFile
          ? this.generalTexts.RECORD_UPDATE_ERROR.label
          : this.generalTexts.FILE_RECORD_UPDATE_ERROR.label,
      },
      delete: {
        label: !isFile
          ? this.generalTexts.RECORD_DELETE_ERROR.label
          : this.generalTexts.FILE_RECORD_DELETE_ERROR.label,
      },
      download: {
        label: this.generalTexts.L_OPERATION_FAILED.label,
      },
      get: {
        label: this.generalTexts.RECORD_GET_ERROR.label,
      },
    };
    const detailLabel = detail || actionLabels[action].label;
    this.addMessage('error', detailLabel, sticky);
  }
}
